/**
 * Escrita que ASSENTA sem esperar o ack do servidor (offline, item 1 de 02/09).
 *
 * O SDK do Firestore grava toda escrita do cliente no cache local na hora — o
 * `onSnapshot` reflete a mudança no mesmo tick, com `fromCache: true` — mas a
 * PROMISE de `setDoc`/`updateDoc`/`deleteDoc`/`batch.commit()` só resolve com
 * o ack do servidor. Sem rede, ela fica pendente até a rede voltar. Medido em
 * 02/09: adicionar livro deixava o sheet em "salvando" para sempre com o livro
 * já visível na estante atrás dele; marcar progresso prendia `isSaving`; a nota
 * ficava em "salvando" eterno; trocar status, criar lista e resenha, idem. O
 * dado estava certo; a UI mentia.
 *
 * `settleWrite` corre a promise contra um teto curto. Ack antes do teto →
 * `"acked"`. Rejeição antes do teto (rules, dado inválido) → propaga, como
 * sempre. Teto vencido → `"queued"`: a escrita está na fila do SDK e será
 * enviada quando houver rede; a UI pode seguir. Uma rejeição TARDIA (depois
 * do teto) não tem mais tela para aparecer, então vai à telemetria — e o
 * `.catch` anexado garante que ela nunca vira unhandled rejection.
 *
 * ⚠️ O que isto NÃO cobre: `runTransaction` (exige servidor e falha ativo,
 * que é o desfecho certo) e callables (`httpsCallable` não é escrita local).
 * E o teto é curto de propósito: online e saudável o ack chega em centenas de
 * ms; 1,5s é "a rede não respondeu", não "a rede está lenta" — nos dois casos
 * o dado já está gravado localmente e o SDK reenvia.
 */
import {
  doc,
  setDoc,
  type CollectionReference,
  type DocumentData,
  type DocumentReference,
  type WithFieldValue,
} from "firebase/firestore";
import { captureException } from "../monitoring";

export const WRITE_SETTLE_MS = 1_500;

export type WriteSettlement = "acked" | "queued";

export async function settleWrite(
  write: Promise<unknown>,
  label: string,
  timeoutMs: number = WRITE_SETTLE_MS,
): Promise<WriteSettlement> {
  let settled = false;
  const pending = Promise.resolve(write);
  return new Promise<WriteSettlement>((resolve, reject) => {
    const timer = setTimeout(() => {
      if (settled) return;
      settled = true;
      // A partir daqui a UI já seguiu: rejeição tardia vira telemetria.
      pending.catch((err) => {
        captureException(err, { scope: "settleWrite.late", label });
      });
      resolve("queued");
    }, timeoutMs);
    pending.then(
      () => {
        if (settled) return;
        settled = true;
        clearTimeout(timer);
        resolve("acked");
      },
      (err) => {
        if (settled) return;
        settled = true;
        clearTimeout(timer);
        reject(err);
      },
    );
  });
}

/**
 * `addDoc` que devolve o id sem esperar o ack.
 *
 * `addDoc` é `doc(col)` + `setDoc` por dentro, mas só entrega a referência no
 * ack — offline, nunca. Gerar o id no cliente (o SDK faz o mesmo) e assentar
 * a escrita pelo `settleWrite` dá à UI o id na hora, que é o que ela precisa
 * para fechar o sheet e apontar para o documento novo.
 */
export async function createDocSettled<T extends DocumentData>(
  col: CollectionReference<T>,
  data: WithFieldValue<T>,
  label: string,
): Promise<DocumentReference<T>> {
  const ref = doc(col);
  await settleWrite(setDoc(ref, data), label);
  return ref;
}
